/**
 * REV-360: heuristic content-category detection from HTML samples.
 *
 * Used by ManualChecklistPage's "Auto-detect" feature to suggest which
 * @afixt/afixt-tests content_category values likely apply to an audit
 * based on the DOM evidence we already have (FindingInstance.element_html
 * snippets, which the platform stored when automated rules fired).
 *
 * The contract is intentionally conservative: we say a category is
 * "detected" only when a DOM-level signal exists. Categories that are
 * purely behavioral (Keyboard, Timeouts) or transverse (CSS, DYN, Mobile)
 * cannot be detected statically — they're listed in
 * NON_DETECTABLE_CATEGORIES so callers know not to auto-hide them.
 *
 * False negatives are real: if no automated rule fired on an image,
 * we won't see <img in the sample — but the page may still have images.
 * The auto-detect button is suggestive, not authoritative; reviewers
 * always have the per-category chip to override.
 */

const CATEGORY_SIGNALS: Record<string, RegExp> = {
  ARIA: /\baria-[a-z]+=|\brole=/i,
  'Document Structure': /<(h[1-6]|header|footer|main|nav|aside|section|article|landmark)\b/i,
  Forms: /<(form|input|select|textarea|fieldset|legend|label|button)\b/i,
  Frames: /<(iframe|frame)\b/i,
  'Images and other non-text content': /<(img|svg|picture|canvas|object|embed)\b/i,
  Language: /\blang=|\bxml:lang=/i,
  Media: /<(video|audio|track|source)\b/i,
  Navigation: /<nav\b|role=["']?navigation/i,
  Tables: /<(table|thead|tbody|tfoot|tr|th|td|caption)\b/i,
};

/** The categories {@link detectContentCategoriesFromHtml} can find in markup. */
export const DETECTABLE_CATEGORIES: readonly string[] = Object.freeze(
  Object.keys(CATEGORY_SIGNALS)
);

/**
 * Categories that exist in the @afixt/afixt-tests catalog but cannot be
 * meaningfully detected from a static HTML sample. These stay visible
 * after auto-detect — applicability is behavioral.
 */
export const NON_DETECTABLE_CATEGORIES: ReadonlySet<string> = new Set([
  'CSS',
  'DYN',
  'Keyboard',
  'Mobile',
  'Timeouts',
]);

/**
 * Inspect a collection of HTML samples and return the set of categories
 * whose DOM signal is present in at least one of them.
 *
 * `htmlSamples` is typically the concatenation of FindingInstance
 * element_html for an audit — small, since FindingInstances only have
 * the failing element snippet rather than full DOM. Performance is fine
 * even with a few hundred samples; the regex set is small.
 */
export function detectContentCategoriesFromHtml(htmlSamples: readonly string[]): Set<string> {
  const detected = new Set<string>();
  if (!htmlSamples.length) {
    return detected;
  }
  // Concatenate once with a safe separator so cross-sample regex matches
  // can't fire across a category boundary.
  const haystack = htmlSamples.filter((s) => typeof s === 'string').join('\n');
  for (const [category, signal] of Object.entries(CATEGORY_SIGNALS)) {
    if (signal.test(haystack)) {
      detected.add(category);
    }
  }
  return detected;
}

/**
 * Given the full set of categories present in the manual checklist and
 * the set detected from HTML samples, return the categories that should
 * be hidden after auto-detect. Categories in NON_DETECTABLE_CATEGORIES
 * are never hidden — their applicability is behavioral and the reviewer
 * has to decide.
 */
export function categoriesToHideAfterAutoDetect(
  allCategories: readonly string[],
  detected: ReadonlySet<string>
): Set<string> {
  const toHide = new Set<string>();
  for (const cat of allCategories) {
    if (NON_DETECTABLE_CATEGORIES.has(cat)) {
      continue;
    }
    if (!detected.has(cat)) {
      toHide.add(cat);
    }
  }
  return toHide;
}
